import type { FuelGroupId } from '../types/gis';

/**
 * Canonical fuel vocabulary.
 *
 * The scraped dataset uses inconsistent brand casing (`JIOBP`, `Jio-bp`) and a
 * long tail of per-brand fuel names (`SPEED 100 BS IV`, `premium_gasoline`,
 * `unleaded_super`). Everything that reads fuel data — the worker filter, the
 * map markers and the filter panel — resolves through this module so the raw
 * values only ever get interpreted in one place.
 */

export interface FuelBrand {
  id: string;
  label: string;
  color: string;
}

/** Brand ids are the normalized form emitted by `normalizeBrand`. */
export const FUEL_BRANDS: FuelBrand[] = [
  { id: 'BPCL', label: 'Bharat Petroleum', color: '#f59e0b' },
  { id: 'NAYARA', label: 'Nayara Energy', color: '#059669' },
  { id: 'JIOBP', label: 'Jio-bp', color: '#4f46e5' },
  { id: 'SHELL', label: 'Shell', color: '#dc2626' },
  { id: 'HPCL', label: 'Hindustan Petroleum', color: '#0284c7' },
  { id: 'IOCL', label: 'Indian Oil', color: '#ea580c' }
];

export const FUEL_BRAND_FALLBACK_COLOR = '#9ca3af';

const BRAND_BY_ID = new Map(FUEL_BRANDS.map(b => [b.id, b]));

const BRAND_LOGOS: Record<string, string> = {
  BPCL: '/assets/fuel-logos/bpcl.webp',
  HPCL: '/assets/fuel-logos/hpcl.webp',
  IOCL: '/assets/fuel-logos/iocl.webp',
  NAYARA: '/assets/fuel-logos/nayara.webp',
  JIOBP: '/assets/fuel-logos/jiobp.webp',
  SHELL: '/assets/fuel-logos/shell.webp'
};

/** `Jio-bp` / `jio bp` / `JIOBP` all collapse to `JIOBP`. */
export const normalizeBrand = (raw: unknown): string =>
  (raw ?? '').toString().toUpperCase().replace(/[^A-Z0-9]/g, '');

export const getBrandLabel = (raw: unknown): string =>
  BRAND_BY_ID.get(normalizeBrand(raw))?.label ?? (raw ?? 'Unknown').toString();

export const getBrandColor = (raw: unknown): string =>
  BRAND_BY_ID.get(normalizeBrand(raw))?.color ?? FUEL_BRAND_FALLBACK_COLOR;

export const getBrandLogo = (raw: unknown): string | null => {
  const norm = normalizeBrand(raw);
  return BRAND_LOGOS[norm] || null;
};

export interface FuelGroup {
  id: FuelGroupId;
  label: string;
  /** Bit position used by the packed statewide index. */
  bit: number;
}

/**
 * `PREMIUM` and `E0` are deliberately separate groups.
 *
 * `PREMIUM` previously carried the label "E0 (100 Octane)" while `classifyFuel` swept every
 * premium grade into it — Power95, TurboJet, XP95, Speed, XtraGreen. On the Tamil Nadu
 * dataset that labelled **3,550** stations as E0 when only **37** actually sell it.
 * Ethanol-free is the specific claim users come here for, so it gets its own group and only
 * genuine E0 products (XP100, Power 100, Speed 100 BS IV) may enter it.
 */
export const FUEL_GROUPS: FuelGroup[] = [
  { id: 'PETROL', label: 'Petrol', bit: 1 },
  { id: 'DIESEL', label: 'Diesel', bit: 2 },
  { id: 'PREMIUM', label: 'Premium', bit: 4 },
  { id: 'EV', label: 'EV Charging', bit: 8 },
  { id: 'E0', label: 'E0 (Ethanol-free)', bit: 16 }
];

const GROUP_BY_ID = new Map(FUEL_GROUPS.map(g => [g.id, g]));

export const getFuelGroupLabel = (id: FuelGroupId): string =>
  GROUP_BY_ID.get(id)?.label ?? id;

/** Every raw `fuels` value observed in the dataset, mapped to its group. */
const RAW_FUEL_TO_GROUP: Record<string, FuelGroupId> = {
  petrol: 'PETROL',
  low_octane_gasoline: 'PETROL',
  midgrade_gasoline: 'PETROL',
  diesel: 'DIESEL',
  shell_regular_diesel: 'DIESEL',
  truck_diesel: 'DIESEL',
  speed: 'PREMIUM',
  premium_gasoline: 'PREMIUM',
  premium_diesel: 'PREMIUM',
  premium_petrol: 'PREMIUM',
  unleaded_super: 'PREMIUM',
  xp95: 'PREMIUM',
  power95: 'PREMIUM',
  power99: 'PREMIUM',
  turbojet: 'PREMIUM',
  xtragreen: 'PREMIUM',
  // The only ethanol-free products across all six brands. IOCL calls it XP100,
  // HPCL Power 100, BPCL Speed 100 BS IV.
  e0: 'E0',
  xp100: 'E0',
  power100: 'E0',
  'power 100': 'E0',
  'speed 100 bs iv': 'E0',
  speed100: 'E0',
  'speed 100': 'E0',
  shell_recharge: 'EV'
};

/**
 * Classify a raw fuel string. Falls back to keyword matching so values the
 * scraper starts emitting later (`XP100`, `XtraGreen`, HPCL's `Power 95`)
 * land in a sensible group instead of silently disappearing from the filters.
 */
export const classifyFuel = (raw: unknown): FuelGroupId | null => {
  const value = (raw ?? '').toString().trim().toLowerCase();
  if (!value) return null;

  const exact = RAW_FUEL_TO_GROUP[value];
  if (exact) return exact;

  if (/recharge|charging|\bev\b/.test(value)) return 'EV';
  // Diesel is checked before the premium keywords so "high speed diesel" — the
  // ordinary grade — is not caught by the "speed" in BPCL's premium petrol.
  if (/diesel/.test(value)) return 'DIESEL';
  // E0 must be tested before PREMIUM: "XP100" matches the premium keyword `xp\s?\d`
  // too, and whichever runs first wins. Only the 100-octane ethanol-free grades
  // qualify — `xp95`, `power99` and the rest fall through to PREMIUM below.
  if (/\b(xp|power|speed)\s?100\b/.test(value)) return 'E0';
  if (/premium|speed|super|power|xp\s?\d|xtra|turbo|octane\s?9/.test(value)) return 'PREMIUM';
  if (/petrol|gasoline|ms\b/.test(value)) return 'PETROL';
  return null;
};

/** Distinct groups offered by a station, in canonical display order. */
export const resolveFuelGroups = (fuels: unknown): FuelGroupId[] => {
  if (!Array.isArray(fuels)) return [];
  const found = new Set<FuelGroupId>();
  for (const fuel of fuels) {
    const group = classifyFuel(fuel);
    if (group) found.add(group);
  }
  // XP100 / Power 100 / Speed 100 are premium grades as well as ethanol-free ones, so an
  // E0 station must also satisfy the Premium filter — otherwise a pump selling only XP100
  // would vanish from a "premium" search.
  if (found.has('E0')) found.add('PREMIUM');
  return FUEL_GROUPS.filter(g => found.has(g.id)).map(g => g.id);
};

/** Pack groups into the bitmask stored in the statewide index. */
export const packFuelGroups = (groups: FuelGroupId[]): number =>
  groups.reduce((mask, id) => mask | (GROUP_BY_ID.get(id)?.bit ?? 0), 0);

export const unpackFuelGroups = (mask: number): FuelGroupId[] =>
  FUEL_GROUPS.filter(g => (mask & g.bit) !== 0).map(g => g.id);

export const maskMatchesGroups = (mask: number, groups: FuelGroupId[]): boolean =>
  groups.length === 0 || groups.some(id => (mask & (GROUP_BY_ID.get(id)?.bit ?? 0)) !== 0);

/** Turn a raw amenity token (`air_and_water`, `ParkingFacility`, `TWO_FOUR_SEVEN`) into a label. */
export const formatAmenity = (raw: unknown): string => {
  const value = (raw ?? '').toString().trim();
  if (!value) return '';
  if (/^two_four_seven$/i.test(value) || /^twenty_four_hour$/i.test(value) || /^24_7$/i.test(value)) return '24/7';
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

/** Turn raw hours (string, array of objects, array of strings) into readable string. */
export const formatHours = (raw: unknown): string => {
  if (!raw) return '';
  if (typeof raw === 'string') return raw.trim();

  if (Array.isArray(raw)) {
    // Check if items are objects with day & hours
    const dayEntries: { day: string; hours: string }[] = [];
    let isStructured = true;

    for (const item of raw) {
      if (typeof item === 'object' && item !== null) {
        const obj = item as Record<string, unknown>;
        const day = (obj.dayOfWeek || obj.day || '').toString().trim();
        const opens = (obj.opens || obj.open || '').toString().trim();
        const closes = (obj.closes || obj.close || '').toString().trim();
        if (day && opens && closes) {
          dayEntries.push({ day, hours: `${opens} - ${closes}` });
          const h = (obj.hours || obj.time || '').toString().trim();
          if (h) dayEntries.push({ day, hours: h });
        } else {
          isStructured = false;
          break;
        }
      } else {
        isStructured = false;
        break;
      }
    }

    if (isStructured && dayEntries.length > 0) {
      // Check if all days have identical hours
      const uniqueHours = Array.from(new Set(dayEntries.map(e => e.hours)));
      if (uniqueHours.length === 1) {
        const singleHours = uniqueHours[0];
        if (/24\s?hours|00:00\s?-\s?23:59|12:00\s?am\s?-\s?11:59\s?pm|12:00\s?am\s?-\s?12:00\s?am|00:00\s?-\s?24:00/i.test(singleHours)) {
          return 'Open 24 Hours (Mon – Sun)';
        }
        return `Mon – Sun: ${singleHours}`;
      }

      // Group consecutive days with matching hours
      const groups: { days: string[]; hours: string }[] = [];
      for (const entry of dayEntries) {
        const lastGroup = groups[groups.length - 1];
        if (lastGroup && lastGroup.hours === entry.hours) {
          lastGroup.days.push(entry.day);
        } else {
          groups.push({ days: [entry.day], hours: entry.hours });
        }
      }

      return groups.map(g => {
        const dayLabel = g.days.length === 1 
          ? g.days[0].slice(0, 3) 
          : `${g.days[0].slice(0, 3)} – ${g.days[g.days.length - 1].slice(0, 3)}`;
        return `${dayLabel}: ${g.hours}`;
      }).join(' • ');
    }

    // Fallback for unstructured string arrays
    const formatted = raw
      .map(item => (typeof item === 'string' ? item.trim() : JSON.stringify(item)))
      .filter(Boolean);
    const unique = Array.from(new Set(formatted));
    return unique.join(', ');
  }

  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    const opens = (obj.opens || obj.open || '').toString().trim();
    const closes = (obj.closes || obj.close || '').toString().trim();
    if (opens && closes) return `${opens} - ${closes}`;
    return Object.values(obj).map(v => (v ?? '').toString().trim()).filter(Boolean).join(' ');
  }

  return String(raw);
};

/** Format and clean up raw scraped fuel station names for display. */
export const formatStationTitle = (rawName: unknown, brand: string, city?: string, address?: string): string => {
  let name = (rawName ?? '').toString().trim();
  const brandLabel = getBrandLabel(brand);

  if (!name || /^hindustan petroleum corporation limited|^hindustan petroleum|^indianoil|^bharat petroleum corporation limited|^bharat petroleum|^nayara energy|^nayara|^shell|^jio-?bp$/i.test(name)) {
    if (address) {
      const parts = address.split(',').map(s => s.trim()).filter(Boolean);
      for (const p of parts) {
        if (p.length > 2 && !/^no\s?\d/i.test(p) && !/^sy\s?\d/i.test(p) && !/india|tamil nadu/i.test(p)) {
          return `${brandLabel} (${p})`;
        }
      }
    }
    if (city) {
      return `${brandLabel} (${city})`;
    }
    return brandLabel;
  }

  // Remove redundant boilerplate prefixes/suffixes
  name = name
    .replace(/^hindustan petroleum corporation limited\s*/i, '')
    .replace(/^indianoil\s*/i, '')
    .replace(/\s*bharat petroleum dealers$/i, '')
    .replace(/\s*bharat petroleum dealer$/i, '')
    .replace(/\s*petrol pump$/i, '')
    .trim();

  if (!name) return brandLabel;

  // Format ALL CAPS names to Title Case if they are long
  if (name === name.toUpperCase() && name.length > 4) {
    name = name.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  }

  return name;
};
